import { StackScreenProps } from "@react-navigation/stack";
import {
  Card,
  FlatActionList,
  Page,
  Switch,
  TextButton,
  useFlashMessages,
  useThemeProvider,
  VStack,
} from "@towersystems/roam-common-native";
import { View } from "react-native-ui-lib";
import { SettingsRootParamList } from "../../../../types";
import { useActiveLayout } from "../../../../utilities/app-state/hooks";
import { useApplyDefaultLayoutConfiguration } from "../../../../utilities/layout";
import { posLayoutResetFailureMessage } from "../../../../utilities/layout/constants";
import { useLocalSettingsProvider } from "../../../../utilities/local-settings";
import { useState } from "react";
import React from "react";

export interface PosLayoutSettingsScreenProps
  extends StackScreenProps<SettingsRootParamList, "PosLayoutSettingsScreen"> {}

export const PosLayoutSettingsScreen = ({
  navigation,
}: PosLayoutSettingsScreenProps) => {
  const [resettingLayout, setResettingLayout] = React.useState(false);
  const { settings, setSetting } = useLocalSettingsProvider();
  const { layout } = useActiveLayout();
  const { showMessage } = useFlashMessages();
  const [isResettingLayout, setIsResettingLayout] = useState(false);
  const { scheme, setScheme } = useThemeProvider();

  const resetPosLayout = useApplyDefaultLayoutConfiguration();

  function handleResetPosLayout() {
    if (!layout) return;

    setIsResettingLayout(true);
    resetPosLayout(layout.id)
      .catch((e) => {
        showMessage(posLayoutResetFailureMessage);
      })
      .finally(() => {
        setIsResettingLayout(false);
      });
  }

  const isDisabled = !layout || resettingLayout;

  const handleOnSchemeChange = (value: boolean) => {
    if (value) {
      setScheme("dark");
    } else {
      setScheme("light");
    }
  };

  return (
    <Page
      title="Local Point of Sale Settings"
      secondaryTitle="Configure your local POS settings that will only apply to this device."
    >
      <VStack>
        <Card title="POS Layout Grid">
          <FlatActionList
            items={[
              {
                key: "allow_layout_edit",
                title: "Allow POS Layout Edit Enabled",
                subTitle: "Enable or disable POS layout edit",
                componentRight: () => {
                  return (
                    <View>
                      <Switch
                        value={settings.allowLayoutEdit}
                        onValueChange={() =>
                          setSetting(
                            "allowLayoutEdit",
                            !settings.allowLayoutEdit
                          )
                        }
                      />
                    </View>
                  );
                },
              },
              {
                destructive: true,
                key: "layout",
                loading: isResettingLayout,
                title: "Reset POS Layout",
                disabled: isDisabled,
                onPress: handleResetPosLayout,
              },
            ]}
          />
        </Card>
        <Card title="Other Settings">
          <FlatActionList
            items={[
              {
                key: "dark_mode",
                title: "Dark Mode",
                subTitle: "Change the theme of the app",
                componentRight: () => {
                  return (
                    <View>
                      <Switch
                        value={scheme == "dark"}
                        onValueChange={handleOnSchemeChange}
                      />
                    </View>
                  );
                },
              },
              {
                key: "print_receipts",
                title: "Automatically Print Receipts",
                subTitle: "Print receipts automatically after a transaction",
                componentRight: () => {
                  return (
                    <View>
                      <Switch
                        value={settings.automaticallyPrintReceipts}
                        onValueChange={() =>
                          setSetting(
                            "automaticallyPrintReceipts",
                            !settings.automaticallyPrintReceipts
                          )
                        }
                      />
                    </View>
                  );
                },
              },
              {
                key: "active_terminal",
                title: `Cart Position`,
                subTitle: "Change the position of the cart",
                onPress: () => navigation.navigate("SelectCartPositionScreen"),
                componentRight: () => {
                  return (
                    <TextButton
                      onPress={() =>
                        navigation.navigate("SelectCartPositionScreen")
                      }
                    >
                      {settings.cartPosition}
                    </TextButton>
                  );
                },
              },
            ]}
          ></FlatActionList>
        </Card>
      </VStack>
    </Page>
  );
};
